<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKanbanAttribute } from 'dashboard/composables/useKanbanAttribute';
import { UNASSIGNED_KANBAN_COLUMN_VALUE } from 'dashboard/constants/kanban';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const { hasKanbanAttribute, kanbanAttributeKey, kanbanAttribute } =
  useKanbanAttribute();

const laneValue = computed(
  () => props.contact.custom_attributes?.[kanbanAttributeKey.value] || ''
);

const laneOptions = computed(() => [
  {
    label: t('CONVERSATION.KANBAN_LANE.UNASSIGNED'),
    value: UNASSIGNED_KANBAN_COLUMN_VALUE,
  },
  ...(kanbanAttribute.value?.attributeValues || []).map(value => ({
    label: value,
    value,
  })),
]);

const laneLabel = computed(
  () => laneValue.value || t('CONVERSATION.KANBAN_LANE.UNASSIGNED')
);

const selectedLaneValue = computed(
  () => laneValue.value || UNASSIGNED_KANBAN_COLUMN_VALUE
);

const handleSelectLane = async (value, hide) => {
  hide();
  if (value === selectedLaneValue.value) return;
  const updatedValue = value === UNASSIGNED_KANBAN_COLUMN_VALUE ? '' : value;
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      customAttributes: {
        [kanbanAttributeKey.value]: updatedValue,
      },
    });
  } catch (error) {
    useAlert(t('CONVERSATION.KANBAN_LANE.UPDATE_ERROR'));
  }
};
</script>

<template>
  <Popover v-if="hasKanbanAttribute" align="start" disable-mobile-view>
    <button
      v-tooltip.top="t('CONVERSATION.KANBAN_LANE.TOOLTIP')"
      type="button"
      class="inline-flex items-center flex-shrink-0 gap-1 px-2 h-6 max-w-32 text-xs font-medium rounded-md outline outline-1 -outline-offset-1 bg-n-iris-2 outline-n-iris-4 text-n-iris-11 hover:bg-n-iris-3 transition-colors"
    >
      <span class="truncate">{{ laneLabel }}</span>
      <Icon icon="i-lucide-chevron-down" class="flex-shrink-0 size-3" />
    </button>
    <template #content="{ hide }">
      <div class="flex flex-col gap-0.5 p-1 min-w-40 max-w-56">
        <Button
          v-for="option in laneOptions"
          :key="option.value"
          :label="option.label"
          size="sm"
          variant="ghost"
          color="slate"
          :icon="option.value === selectedLaneValue ? 'i-lucide-check' : ''"
          class="!justify-start !px-2 w-full"
          @click="handleSelectLane(option.value, hide)"
        />
      </div>
    </template>
  </Popover>
</template>
